import {
  getMenuTagDefinition,
  hydrateMenuTagRefs,
  partitionDietaryAndAdditiveIds,
  resolveMenuItemLegalMarks,
  type MenuLegalMark,
} from '@web-studio/shared-schemas';
import type { MenuColumn } from './parseMenuColumns';

export type MenuLegendDietaryEntry = {
  id: string;
  label: string;
  iconKey: string | null;
};

function sortLegalMarks(a: MenuLegalMark, b: MenuLegalMark): number {
  if (a.kind !== b.kind) {
    return a.kind === 'allergen' ? -1 : 1;
  }
  if (a.kind === 'allergen') {
    return a.code.localeCompare(b.code);
  }
  return Number(a.code) - Number(b.code) || a.code.localeCompare(b.code);
}

/**
 * Unique allergen/additive footnote marks used on this menu (Kennzeichnung).
 */
export function collectMenuLegalLegendMarks(
  columns: readonly MenuColumn[],
  locale: string,
): MenuLegalMark[] {
  const byKey = new Map<string, MenuLegalMark>();

  for (const column of columns) {
    for (const item of column.items) {
      const partitioned = partitionDietaryAndAdditiveIds(item.dietaryTagIds ?? []);
      const additiveIds = [...(item.additiveIds ?? []), ...partitioned.additive];

      for (const mark of resolveMenuItemLegalMarks(
        item.allergenIds,
        additiveIds,
        locale,
      )) {
        byKey.set(`${mark.kind}:${mark.code}`, mark);
      }

      for (const id of item.allergenIds ?? []) {
        const match = /^legend_allergen_(.+)$/i.exec(id);
        if (!match?.[1]) continue;
        const code = match[1].toUpperCase();
        const key = `allergen:${code}`;
        if (!byKey.has(key)) {
          byKey.set(key, { code, label: code, kind: 'allergen' });
        }
      }
      for (const id of additiveIds) {
        const match = /^legend_additive_(.+)$/i.exec(id);
        if (!match?.[1]) continue;
        const code = match[1];
        const key = `additive:${code}`;
        if (!byKey.has(key)) {
          byKey.set(key, { code, label: code, kind: 'additive' });
        }
      }
    }
  }

  return [...byKey.values()].sort(sortLegalMarks);
}

/**
 * Unique dietary claims used on this menu — icon + label for the footnote key
 * (chips may be icon-led on some themes).
 */
export function collectMenuLegalLegendDietary(
  columns: readonly MenuColumn[],
  locale: string,
): MenuLegendDietaryEntry[] {
  const byId = new Map<string, MenuLegendDietaryEntry>();

  for (const column of columns) {
    for (const item of column.items) {
      const partitioned = partitionDietaryAndAdditiveIds(item.dietaryTagIds ?? []);
      const dietaryIds = [
        ...partitioned.dietary,
        ...partitioned.other.filter((id) => /^legend_dietary_/i.test(id)),
      ];
      for (const ref of hydrateMenuTagRefs(dietaryIds, locale)) {
        const def = getMenuTagDefinition(ref.id);
        if (def && def.kind !== 'dietary') {
          continue;
        }
        if (!byId.has(ref.id)) {
          byId.set(ref.id, {
            id: ref.id,
            label: ref.label,
            iconKey: ref.iconKey,
          });
        }
      }
    }
  }

  return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, locale));
}
